import React, { useEffect, useState } from 'react';
import { commercialTemplates } from './commercialTemplates';

const fallbackTimezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
];

const fallbackLanguages = [
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'es-US', label: 'Spanish (United States)' },
];

const initialForm = {
  companyName: '',
  companyDomain: '',
  restrictSignupToCompanyDomain: false,
  templateType: 'asset_maintenance',
  timezone: 'UTC',
  locale: 'en-US',
};

const companyDomainExample = 'example.com';

function normalizeCompanyDomain(value) {
  const trimmed = (value || '').trim().toLowerCase();
  if (!trimmed) return '';

  let normalized = trimmed.replace(/^@+/, '');
  normalized = normalized.replace(/^https?:\/\//, '');
  normalized = normalized.split('/')[0].split(':')[0];
  normalized = normalized.replace(/\.+$/, '');
  return normalized;
}

function isValidCompanyDomain(value) {
  const normalized = normalizeCompanyDomain(value);
  if (!normalized || normalized.includes(' ') || !normalized.includes('.')) return false;

  const labels = normalized.split('.');
  if (labels.some(label => !label)) return false;

  const labelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  const topLevelDomain = labels[labels.length - 1];

  return topLevelDomain.length >= 2 && /^[a-z]+$/.test(topLevelDomain) && labels.every(label => labelPattern.test(label));
}

export default function CommercialSetupWizard({ api, onTenantCreated, creatorEmail }) {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    templates: commercialTemplates,
    timezones: fallbackTimezones,
    languages: fallbackLanguages,
    defaultTimezone: 'UTC',
    defaultLocale: 'en-US',
  });
  const [error, setError] = useState('');
  const [createdTenant, setCreatedTenant] = useState(null);
  const [domainTouched, setDomainTouched] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      if (!api) return;

      try {
        const res = await api.get('/commercial/setup/options');
        if (!isMounted) return;

        setOptions(res.data);
        setForm(current => ({
          ...current,
          templateType: res.data.defaultTemplateType || current.templateType,
          timezone: res.data.defaultTimezone || current.timezone,
          locale: res.data.defaultLocale || current.locale,
        }));
      } catch (err) {
        if (!isMounted) return;
        setError(err.response?.data?.message || 'Unable to load commercial setup options.');
      }
    };

    loadOptions();
    return () => {
      isMounted = false;
    };
  }, [api]);

  const selectedTemplate =
    options.templates.find(template => template.key === form.templateType) || options.templates[0];
  const normalizedCompanyDomain = normalizeCompanyDomain(form.companyDomain);
  const showDomainRestriction = Boolean(normalizedCompanyDomain);
  const hasInvalidCompanyDomain = Boolean(form.companyDomain.trim()) && !isValidCompanyDomain(form.companyDomain);

  const handleSubmit = async event => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setDomainTouched(true);

    if (hasInvalidCompanyDomain) {
      setError(`Company domain must look like ${companyDomainExample}.`);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...form,
        email: creatorEmail,
        companyDomain: normalizedCompanyDomain,
        restrictSignupToCompanyDomain: showDomainRestriction ? form.restrictSignupToCompanyDomain : false,
      };
      const res = await api.post('/commercial/setup/tenant', payload);
      setCreatedTenant(res.data);
      onTenantCreated?.(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to create commercial tenant.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="commercial-panel">
      <p className="commercial-panel__eyebrow">Commercial Setup</p>
      <h2 className="commercial-panel__title">Create Your Company Workspace</h2>
      <p className="commercial-panel__copy">
        This setup flow creates your company workspace, default roles, a main location, an operations team, and a starter shift.
      </p>

      <form onSubmit={handleSubmit} className="commercial-form" style={{ marginTop: 16 }}>
        <label className="commercial-form__field">
          <span>Company Name</span>
          <input
            type="text"
            value={form.companyName}
            onChange={event => setForm(current => ({ ...current, companyName: event.target.value }))}
            placeholder="Example: Mission Focused Ethos, LLC"
          />
        </label>

        <label className="commercial-form__field">
          <span>Company Domain</span>
          <input
            type="text"
            value={form.companyDomain}
            onBlur={() => setDomainTouched(true)}
            onChange={event =>
              setForm(current => ({
                ...current,
                companyDomain: event.target.value,
                restrictSignupToCompanyDomain:
                  event.target.value.trim() ? current.restrictSignupToCompanyDomain : false,
              }))
            }
            placeholder={companyDomainExample}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <small className="commercial-form__hint">
            Optional. Example: <strong>{companyDomainExample}</strong>. If provided, it can be used for branding and
            company sign-up rules.
          </small>
          {domainTouched && hasInvalidCompanyDomain ? (
            <small className="commercial-form__hint commercial-form__hint--error">
              Enter a valid domain like <strong>{companyDomainExample}</strong>.
            </small>
          ) : null}
        </label>

        {showDomainRestriction ? (
          <label className="commercial-toggle">
            <input
              type="checkbox"
              checked={form.restrictSignupToCompanyDomain}
              onChange={event =>
                setForm(current => ({
                  ...current,
                  restrictSignupToCompanyDomain: event.target.checked,
                }))
              }
            />
            <span className="commercial-toggle__track" aria-hidden="true">
              <span className="commercial-toggle__thumb" />
            </span>
            <span className="commercial-toggle__copy">
              Only allow future self-signups for this company when the user email matches <strong>{normalizedCompanyDomain}</strong>.
            </span>
          </label>
        ) : null}

        <label className="commercial-form__field">
          <span>Template Type</span>
          <select
            value={form.templateType}
            onChange={event => setForm(current => ({ ...current, templateType: event.target.value }))}
          >
            {options.templates.map(template => (
              <option key={template.key} value={template.key}>
                {template.label}
              </option>
            ))}
          </select>
        </label>

        <div className="commercial-form__grid">
          <label className="commercial-form__field">
            <span>Timezone</span>
            <select
              value={form.timezone}
              onChange={event => setForm(current => ({ ...current, timezone: event.target.value }))}
            >
              {options.timezones.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="commercial-form__field">
            <span>Language</span>
            <select
              value={form.locale}
              onChange={event => setForm(current => ({ ...current, locale: event.target.value }))}
            >
              {options.languages.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {selectedTemplate ? (
          <div className="commercial-template-preview">
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>{selectedTemplate.label}</h3>
            <p style={{ marginTop: 0, color: '#4d657d' }}>{selectedTemplate.description}</p>
            <p style={{ marginBottom: 8, fontWeight: 600, color: '#173047' }}>Starter Modules</p>
            <div className="commercial-pill-group">
              {selectedTemplate.modules.map(moduleName => (
                <span key={moduleName} className="commercial-pill">
                  {moduleName}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {error ? <div className="commercial-error">{error}</div> : null}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="submit" disabled={loading}>
            {loading ? 'Creating Workspace...' : 'Create Company Workspace'}
          </button>
          {createdTenant?.tenant ? (
            <span className="commercial-inline-feedback">
              Created {createdTenant.tenant.companyName}
            </span>
          ) : null}
        </div>
      </form>
    </section>
  );
}
